// Copyright (c) 2026 杨翔宇（武汉文理学院）
// 物理智能教学智能体平台 (Physics Intelligent Agent)
// 保留所有权利 (All Rights Reserved)。未经著作权人书面许可，
// 不得以任何形式复制、传播、修改或用于商业用途。

import type { Session } from '@/types';
import { extractUserFacingMessage } from '@api/errorMessage';
import { resolveRetryDelayMs } from '@api/retryPolicy';
import { SESSIONS_CACHE_TTL_MS } from './chatStore.cache';

interface SessionListSnapshot {
  sessions: Session[];
  sessionsLoadedAt: number;
  isLoadingSessions: boolean;
  backgroundError: string | null;
  chatError: string | null;
  sessionsTotal: number;
}

interface SessionListPatch {
  sessions?: Session[];
  sessionsLoadedAt?: number;
  isLoadingSessions?: boolean;
  backgroundError?: string | null;
  chatError?: string | null;
  sessionsTotal?: number;
}

type SessionListSetState = (
  patchOrUpdater: SessionListPatch | ((state: SessionListSnapshot) => SessionListPatch)
) => void;

export type FetchSessionsResult = Session[] | { sessions: Session[]; total: number };

export interface SessionListOrchestratorOptions {
  getState: () => SessionListSnapshot;
  setState: SessionListSetState;
  fetchSessions: () => Promise<FetchSessionsResult>;
  now?: () => number;
  ttlMs?: number;
  /** Test seam for the failure auto-retry; defaults to window setTimeout. */
  scheduleRetry?: (callback: () => void, delayMs: number) => void;
}

interface InFlightSessionListLoad {
  version: number;
  requestId: number;
  promise: Promise<void>;
}

/**
 * Thin orchestration contract for the session list slice.
 *
 * - `load()` owns fetch deduplication, TTL short-circuit, request/version
 *   guards, and the three setState transitions of a fetch (start, resolve,
 *   finalize).
 * - `applyMutation(reducer)` owns the write coordination that previously
 *   leaked into chatStore.ts as a two-phase pattern (`invalidate()` + a
 *   separate `set(reducer)` call). It bumps the mutation version, synchronously
 *   drops any in-flight bookkeeping so the next load re-fetches, and merges
 *   the reducer's business patch with loading-state cleanup into a single
 *   setState invocation. Business reducers (applyAuthReconciled,
 *   applySessionCreated, ...) remain intact; they are invoked once and their
 *   returned patch is not decomposed.
 */
export interface SessionListOrchestrator {
  load: () => Promise<void>;
  applyMutation: (reducer: (state: SessionListSnapshot) => SessionListPatch) => void;
}

export function createSessionListOrchestrator(
  options: SessionListOrchestratorOptions
): SessionListOrchestrator {
  const now = options.now ?? Date.now;
  const ttlMs = options.ttlMs ?? SESSIONS_CACHE_TTL_MS;
  const scheduleRetry =
    options.scheduleRetry ??
    ((callback: () => void, delayMs: number) => {
      setTimeout(callback, delayMs);
    });
  let mutationVersion = 0;
  let requestSequence = 0;
  let activeRequestId = 0;
  let inFlight: InFlightSessionListLoad | null = null;
  // 失败退避窗口：失败后到下一次定时重试之间，任何 load() 调用都被吸收，
  // 保证「一次失败 → 恰好一次到点重试」，不会反复打到正在 429 的端点。
  let retryNotBefore = 0;

  const isCacheFresh = () => {
    const loadedAt = options.getState().sessionsLoadedAt;
    return loadedAt > 0 && now() - loadedAt < ttlMs;
  };

  const abortInFlight = (): boolean => {
    mutationVersion += 1;
    if (!inFlight) {
      return false;
    }
    activeRequestId = 0;
    inFlight = null;
    return true;
  };

  const load = (): Promise<void> => {
    if (inFlight?.version === mutationVersion) {
      return inFlight.promise;
    }

    if (isCacheFresh()) {
      return Promise.resolve();
    }

    if (now() < retryNotBefore) {
      return Promise.resolve();
    }

    const requestMutationVersion = mutationVersion;
    const requestId = requestSequence + 1;
    requestSequence = requestId;
    activeRequestId = requestId;
    options.setState({ isLoadingSessions: true });

    const promise = (async () => {
      try {
        const rawResult = await options.fetchSessions();
        if (requestMutationVersion !== mutationVersion || activeRequestId !== requestId) {
          return;
        }
        const sessions = Array.isArray(rawResult) ? rawResult : rawResult.sessions;
        const total = Array.isArray(rawResult) ? sessions.length : rawResult.total;
        options.setState({
          sessions,
          sessionsTotal: total,
          sessionsLoadedAt: now(),
          backgroundError: null,
        });
      } catch (error) {
        if (requestMutationVersion !== mutationVersion || activeRequestId !== requestId) {
          return;
        }
        // 失败不清空列表：保留既有 sessions/total/sessionsLoadedAt（不把
        // 缓存标新），只记录后台错误，并安排一次到点自动重试。
        const retryDelayMs = resolveRetryDelayMs(error);
        retryNotBefore = now() + retryDelayMs;
        options.setState({
          backgroundError: extractUserFacingMessage(error),
        });
        scheduleRetry(() => {
          void load();
        }, retryDelayMs);
      } finally {
        if (activeRequestId === requestId) {
          options.setState({ isLoadingSessions: false });
        }
        if (inFlight?.requestId === requestId) {
          inFlight = null;
        }
      }
    })();

    inFlight = {
      version: requestMutationVersion,
      requestId,
      promise,
    };
    return promise;
  };

  return {
    load,
    applyMutation: (reducer) => {
      const hadInFlight = abortInFlight();
      options.setState((state) => {
        const reducerPatch = reducer(state);
        return hadInFlight ? { isLoadingSessions: false, ...reducerPatch } : reducerPatch;
      });
    },
  };
}
